export type DataMode = 'online' | 'offline';

export type Geography = { id: string; name: string; code: string };

/** Top-bar selector value — `'all'` means "no geography filter active". */
export type GeoSelection = string;
export const GEO_ALL = 'all';

export type Profile = {
  full_name: string;
  email: string;
  /** `'all'` allowed for users who span multiple geographies. */
  geography: GeoSelection;
};

export type AppConfig = {
  servicenow_test_url: string;
  geographies: Geography[];
  data_mode: DataMode;
  snapshot: { last_sync_at: string | null; counts: Record<string, number> };
};

export type SyncJob = {
  id: string;
  status: 'running' | 'completed' | 'failed';
  started_at: string;
  finished_at: string | null;
  error: string | null;
  current_phase: string | null;
  /** 1..23 — see spec §7.2 phase list. */
  current_step: number | null;
  steps_total: number | null;
  /** Currently-fetching ServiceNow table, when phase is a fetch_*. */
  table: string | null;
  table_index: number | null;
  tables_total: number | null;
  records_total: number | null;
  records_done: number;
  elapsed_ms: number | null;
  eta_ms: number | null;
  counts: Record<string, number>;
  /** Legacy: same as records_total/records_done; kept for the inline bar. */
  total: number | null;
  done: number;
};

/** Per-phase event log entry, captured for the SyncProgressModal. */
export type SyncLogEntry = {
  ts: number;
  kind: 'phase_start' | 'phase_end' | 'progress' | 'warning' | 'info' | 'skipped';
  phase: string;
  message: string;
};

/** Per-phase progress, accumulated by the sync controller from SSE events. */
export type PhaseProgress = {
  status: 'pending' | 'running' | 'completed' | 'skipped' | 'failed';
  records_total: number | null;
  records_done: number;
  records_written: number | null;
  elapsed_ms: number | null;
  eta_ms: number | null;
  started_at: number | null;
  finished_at: number | null;
  /** When skipped, age (in hours) of the cached entry the orchestrator reused. */
  skipped_age_hours: number | null;
  /** Sub-phases observed under this descriptor (e.g. fetch_slas[P2P]). */
  sub_phases: string[];
  error: string | null;
};

/** One row of the phase_state table — last-known per-fetch-call result. */
export type PhaseStateRow = {
  phase: string;
  sn_table: string;
  in_field: string;
  status: string;
  completed_at: string | null;
  input_fingerprint: string;
  row_count: number | null;
  error: string | null;
};

export type SyncStatus = {
  running: SyncJob | null;
  last_sync_at: string | null;
  counts: Record<string, number>;
  data_mode: DataMode;
  has_snapshot: boolean;
  phase_state?: PhaseStateRow[];
};

export type GapScope = 'form' | 'stream';
export type GapType =
  | 'new_form_required'
  | 'change_form_fields'
  | 'change_workflow_approvals'
  | 'other_changes';

export type Gap = {
  id: string;
  scope: GapScope;
  stream: string;
  geography: string;
  kb_number: string | null;
  form_sys_id: string | null;
  form_name_snapshot: string | null;
  gap_type: GapType;
  proposed_form_name: string | null;
  /** Optional pointer at a specific variable when scope='form' and gap_type='change_form_fields'. */
  field_name: string | null;
  description: string;
  author_name: string;
  author_email: string | null;
  created_at: string;
  updated_at: string;
};

export type GapCreateInput = Omit<Gap, 'id' | 'created_at' | 'updated_at'>;
export type GapPatchInput = Partial<
  Pick<Gap, 'gap_type' | 'description' | 'proposed_form_name' | 'geography'>
>;

/**
 * Per-geography review status. `pending_review` is expressed by row absence
 * on the server; stored rows always carry one of the completed variants.
 */
export type ReviewStatus =
  | 'pending_review'
  | 'review_completed_form_required'
  | 'review_completed_form_not_required';
export type CompletedReviewStatus = Exclude<ReviewStatus, 'pending_review'>;

export type Review = {
  kb_number: string;
  geography: string;
  stream: string | null;
  form_sys_id: string | null;
  reviewer_name: string;
  reviewer_email: string | null;
  reviewed_at: string;
  status: ReviewStatus;
};

// ─── Structured form view (spec §8.2) ───────────────────────────────────

export type FieldKind =
  | 'choice_list'
  | 'reference_db'
  | 'reference_ref'
  | 'free_text'
  | 'other';

export type FormFieldChoice = {
  text: string | null;
  value: string | null;
  order: number | null;
  /** 'standard' = available to everyone, 'localization' = added in a
   * country branch of a catalog client script, 'unknown' otherwise. */
  localization_status?: 'standard' | 'localization' | 'unknown';
  /** Country names this choice was found added for (when known). */
  available_in_countries?: string[];
  /** True when the dropdown is dynamically rebuilt at runtime via
   * GlideAjax / `clearOptions`. Per-value localization can't be parsed
   * statically in that case. */
  rebuilt_at_runtime?: boolean;
};

/** Output of `api.dynamics_parser` — one entry per (field × source). */
export type FormFieldDynamic = {
  variable_name: string | null;
  source_kind: 'client_script' | 'ui_policy';
  source_sys_id: string;
  source_name: string | null;
  classification: 'localization' | 'cross_field' | 'static' | 'unknown';
  effect:
    | 'visibility'
    | 'mandatory'
    | 'readonly'
    | 'value'
    | 'choice_filter'
    | 'other';
  trigger_type: string | null;
  countries_visible: string[];
  countries_hidden: string[];
  added_choices: { value: string; label: string; countries: string[] }[];
  removed_choices: { value: string; countries: string[] }[];
  choices_rebuilt_at_runtime: boolean;
  notes: string | null;
};

/** Per-field roll-up of dynamics rows — drives the section split. */
export type FormFieldLocalization = {
  /** 'universal' = always visible, 'country_specific' = visibility gated
   * by country, 'unknown' = parser couldn't classify a known source. */
  placement: 'universal' | 'country_specific' | 'unknown';
  /** Country names the field is shown in (when placement is country_specific). */
  visible_countries: string[];
  /** Country names the field is hidden in. */
  hidden_countries: string[];
  /** True if any source manipulates the choice list. */
  choice_filter: boolean;
  /** True if at least one source we matched is unclassified. */
  has_unknown: boolean;
  sources: {
    kind: string | null;
    name: string | null;
    classification: string | null;
    effect: string | null;
  }[];
};

export type FormField = {
  sys_id: string;
  order: number | null;
  name: string;
  question_text: string | null;
  type_id: number | null;
  type_name: string | null;
  field_kind: FieldKind;
  mandatory: boolean;
  read_only: boolean;
  default_value: string | null;
  help_text: string | null;
  instructions: string | null;
  reference_table: string | null;
  reference_label: string | null;
  choices: FormFieldChoice[] | null;
  has_conditional_logic: boolean;
  conditional_sources: string[];
  variable_set_sys_id: string | null;
  variable_set_title: string | null;
  variable_set_name: string | null;
  dynamics?: FormFieldDynamic[];
  localization?: FormFieldLocalization;
};

export type FormIdentity = {
  producer_sys_id: string;
  name: string | null;
  kb_number: string | null;
  stream: string | null;
  category: string | null;
  catalog: string | null;
  priority: string | null;
  available_for: string | null;
  /** Assignment group as declared in the KB article ("Assignment Group"
   * row of the General Configuration table). Live values surface in the
   * Assignment Group section below; this is the design-doc default. */
  assignment_group_default: string | null;
  last_synced_at: string | null;
  has_kb_article: boolean;
  is_core: boolean;
  short_description: string | null;
  description: string | null;
  table_name: string | null;
};

/** Country-availability row from the KB structured JSON.
 * `enabled=true` means the form is supposed to be activated for that
 * geography per the Core-Model design. */
export type FormCountryAvailability = {
  country: string;
  enabled: boolean;
};

export type FormAssignmentGroup = {
  sys_id: string;
  name: string | null;
  manager?: string | null;
  email?: string | null;
  members: {
    sys_id: string;
    name: string | null;
    email: string | null;
    user_name: string | null;
  }[];
  /** Ticket count for this AG on this producer (when known). */
  ticket_count: number | null;
  /** Routing-rule name that matched the requested geography, e.g.
   * "Procure to Pay - Universal Request Routing - USA / Canada".
   * Null when no geography was supplied or no rule matched. */
  assignment_rule: string | null;
};

/** `area_sla` rows come from `u_{area}_request_sla` (the authoritative
 * Danone source). `contract_sla` is the generic ServiceNow fallback,
 * shown only when no area-specific row covers the producer. */
export type FormSLASource = 'area_sla' | 'contract_sla';

export type FormSLA = {
  name: string | null;
  type: string | null;
  duration: string | null;
  duration_type: string | null;
  start_condition: string | null;
  stop_condition: string | null;
  pause_condition: string | null;
  source: FormSLASource;
};

/** A workflow / approval step lifted from the KB article's structured JSON.
 * Real Flow Designer mapping arrives in a follow-up phase; the shape stays
 * stable so the UI doesn't churn when the source is upgraded. */
export type FormApproval = {
  order: string | number | null;
  type: string | null;
  description: string | null;
  assignment_group: string | null;
  criteria: string | null;
  /** "core" — part of the Core Model flow.
   *  "country" — country/region-specific exception (heuristic on criteria
   *  text; see `_classify_approval_scope` in the API). */
  scope: 'core' | 'country';
};

export type FormView = {
  identity: FormIdentity;
  fields: FormField[];
  assignment_group: FormAssignmentGroup | null;
  approvals: FormApproval[];
  slas: FormSLA[];
  /** True iff the KB structured JSON declares conditional logic on at
   *  least one variable (per-field badges are no longer surfaced; one
   *  form-level warning is shown above the fields table instead). */
  has_conditional_logic: boolean;
  /** Country availability parsed from the KB's "Core Model Intake Form
   *  Enabled For" table. May be empty when the KB doesn't declare it. */
  countries: FormCountryAvailability[];
  warnings: string[];
};

// ─── KB drawer (spec §8.3 + §13 Phase 1/2) ──────────────────────────────

/** General-config block in `structured_json`. Each key maps to either a
 * raw string (older shape) or `{value, href?}` (newer shape). */
export type KbStructuredConfigEntry = string | { value: string | null; href?: string | null };

export type KbStructuredField = {
  order: string | number | null;
  name: string;
  type: string | null;
  help_text: string | null;
  mandatory: boolean;
  default_value: string | null;
  conditional_logic: string | null;
  choices: string[];
  notes: string | null;
};

export type KbStructuredWorkflowStep = {
  step?: string | number | null;
  name?: string | null;
  description?: string | null;
  trigger?: string | null;
  [key: string]: unknown;
};

export type KbStructuredNotification = {
  stage: string | null;
  name: string | null;
  aim: string | null;
  sent_from: string | null;
  triggered_when: string | null;
  send_to: string | null;
  subject: string | null;
  body: string | null;
};

export type KbStructuredCountry = { country: string; enabled: boolean };

export type KbStructuredChangelogEntry = {
  date: string | null;
  request_number: string | null;
  element_modified: string | null;
  description: string | null;
  request_href: string | null;
};

export type KbStructuredJson = {
  has_structured_data?: boolean;
  general_config?: Record<string, KbStructuredConfigEntry>;
  fields?: KbStructuredField[];
  workflow?: KbStructuredWorkflowStep[];
  notifications?: KbStructuredNotification[];
  countries?: KbStructuredCountry[];
  changelog?: KbStructuredChangelogEntry[];
};

export type KbDrawerPayload = {
  kb_number: string;
  sys_id: string | null;
  title: string;
  short_description: string | null;
  body_html: string;
  /** Parsed `kb_articles.structured_json`. Empty object when nothing was parsed. */
  structured_json: KbStructuredJson;
  /** Mirrors `structured_json.has_structured_data`; cheap top-level flag for UI gating. */
  has_structured_data: boolean;
  fetched_at: string | null;
};

export type ReviewCreateInput = {
  kb_number: string;
  /** `'all'` fans out server-side into one row per configured geography. */
  geography: string;
  status: CompletedReviewStatus;
  reviewer_name: string;
  reviewer_email?: string | null;
  stream?: string | null;
  form_sys_id?: string | null;
};

// ─── Admin sync-history (spec §6.2 / §14 Phase 4) ──────────────────────

export type SyncJobHistoryRow = {
  id: string;
  started_at: string;
  finished_at: string | null;
  status: 'running' | 'completed' | 'failed';
  error: string | null;
  triggered_by: number | null;
  triggered_by_name: string | null;
  triggered_by_email: string | null;
  counts: Record<string, number>;
};

// ─── Snapshot-driven streams + forms-list (spec §8.2) ───────────────────

export type StreamSummary = {
  id: string;
  name: string;
  color: string | null;
  form_count_core: number;
  form_count_total: number;
  /** `false` for DBS apps that the sync still pulls (BSRP, CAT, BRM, DSP)
   * but that the AME LPO is not asked to review. Hidden by default; the
   * StreamsIndex shows them via the "Show all streams" toggle. */
  in_scope: boolean;
};

/** ServiceNow `sc_cat_item_user_criteria_no_mtom` rows give us a count
 * of country/group restrictions on a producer, but resolving the
 * user_criteria sys_ids to human-readable country names is a future sync
 * phase. Until then, the API returns `available_geographies: null` and
 * a coarse `availability_status` so the UI can distinguish "open" from
 * "restricted but mapping not yet available". */
export type AvailabilityStatus = 'open' | 'restricted' | 'unknown';

export type StreamFormRow = {
  kb_number: string | null;
  producer_sys_id: string;
  name: string | null;
  code: string | null;
  short_description: string | null;
  category: string | null;
  table_name: string | null;
  is_core: boolean;
  available_geographies: string[] | null;
  availability_status: AvailabilityStatus;
  availability_criteria_count: number;
  /** Map of geography_code → completed review status (rows present only when
   * a review exists; absence means `pending_review`). */
  review_status_per_geo: Record<string, CompletedReviewStatus>;
};
